import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Input,
  IconButton,
  Table,
  Button,
  Sheet,
  CircularProgress,
} from '@mui/joy';
import {
  Trash,
  Pencil,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Ban,
  BadgeEuro,
  HeartHandshake,
} from 'lucide-react';
import ModaleUtente from '../../components/ModaleUtente';
import { authFetch } from '../../utils/authFetch';
import { canEditUtenti } from '../../utils/permissions';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ModaleConferma from '../../components/ModaleConferma';

export default function Utenti() {
  const [showModale, setShowModale] = useState(false);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 20;

  const [sortConfig, setSortConfig] = useState({ key: 'nome', direction: 'asc' });

  const selectedOrg = localStorage.getItem('selectedOrg');

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const ruoloLabels = {
    0: { label: 'Basic', color: 'neutral' },
    1: { label: 'Visualizzatore', color: 'primary' },
    2: { label: 'Admin', color: 'danger' },
  };

  const userTypeLabels = {
    1: { label: 'Volontario', icon: HeartHandshake, color: 'success' },
    2: { label: 'Dipendente', icon: BadgeEuro, color: 'warning' },
  };

  const fetchUsers = useCallback(async () => {
    if (!selectedOrg) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Errore fetch utenti');

      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore nel fetch utenti:', err);
      setUsers([]);
      toast.error('Errore nel caricamento degli utenti', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedOrg]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const normalize = (v) => (v ?? '').toString().toLowerCase();

  const filteredUsers = useMemo(() => {
    const term = normalize(searchTerm);
    if (!term) return users;

    return users.filter((u) =>
      normalize(u.nome).includes(term) ||
      normalize(u.username).includes(term) ||
      normalize(u.codice_fiscale).includes(term)
    );
  }, [users, searchTerm]);

  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;

    const getComparable = (u, key) => {
      const val = u?.[key];
      if (
        key === 'id' ||
        key === 'livello_permesso_massimo' ||
        key === 'user_type'
      ) {
        return Number(val) || 0;
      }
      return normalize(val);
    };

    return [...filteredUsers].sort((a, b) => {
      const A = getComparable(a, sortConfig.key);
      const B = getComparable(b, sortConfig.key);
      if (A < B) return -1 * dir;
      if (A > B) return 1 * dir;
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / perPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedUsers.slice(start, start + perPage);
  }, [page, sortedUsers]);

  const handleSort = (key) => {
    setPage(1);
    setSortConfig((cur) => {
      if (cur.key === key) {
        return { key, direction: cur.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowDown size={14} color="#bbb" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={14} color="black" />
      : <ArrowDown size={14} color="black" />;
  };

  const handleToggleStatus = async (user) => {
    if (!user?.id || !selectedOrg) return;

    const current = user.user_status ?? user.status ?? 0;
    const nuovoStatus = current === 1 ? 0 : 1;

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${user.id}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            status: nuovoStatus,
            user_id: parseInt(user.id, 10),
            org_id: parseInt(selectedOrg, 10),
          }),
        }
      );

      if (!res.ok) throw new Error('Errore nella modifica dello stato utente');

      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id
            ? { ...u, status: nuovoStatus, user_status: nuovoStatus }
            : u
        )
      );

      toast.success(
        nuovoStatus === 1 ? 'Utente attivato con successo' : 'Utente disattivato con successo',
        { position: 'top-right', autoClose: 2500 }
      );
    } catch (err) {
      console.error(err);
      toast.error('Errore nel cambio di stato utente', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const handleConfirmDelete = async () => {
    const id = userToDelete?.id;
    if (!id) return;

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/user/${id}/unlink`,
        { method: 'DELETE', credentials: 'include' }
      );

      if (!res.ok) throw new Error('Errore nella rimozione');

      toast.success('Utente rimosso con successo', {
        position: 'top-right',
        autoClose: 2500,
      });
      fetchUsers();
    } catch (err) {
      console.error(err);
      toast.error('Errore nella rimozione dell’utente', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setConfirmDeleteOpen(false);
      setUserToDelete(null);
    }
  };

  return (
    <Box>
      <Typography level="h3" mb={2}>
        Utenti
      </Typography>

      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Input
            placeholder="Cerca per nome, username, CF..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            sx={{ maxWidth: 420 }}
          />
          <Typography level="body-sm" color="neutral">
            {sortedUsers.length} record trovati
          </Typography>
        </Box>

        {canEditUtenti() && (
          <Button variant="solid" color="primary" onClick={() => setShowModale(true)}>
            + Aggiungi Utente
          </Button>
        )}
      </Box>

      <Sheet
        variant="outlined"
        sx={{
          borderRadius: 'lg',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'neutral.outlinedBorder',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : sortedUsers.length === 0 ? (
          <Box p={3}>
            <Typography>Nessun utente trovato.</Typography>
          </Box>
        ) : (
          <Table
            size="sm"
            sx={{
              minWidth: { xs: '100%', sm: 850 },
              tableLayout: 'auto',
              '& thead th': {
                bgcolor: 'neutral.softBg',
                textAlign: 'left',
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 1 },
                fontSize: { xs: 'xs', sm: 'sm' },
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              },
              '& tbody td': {
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 1 },
                fontSize: { xs: 'sm', sm: 'md' },
                lineHeight: 1.2,
                wordBreak: 'break-word',
                verticalAlign: 'middle',
              },
              '& tbody tr:hover': { bgcolor: 'neutral.plainHoverBg' },
            }}
          >
            <thead>
              <tr>
                <th onClick={() => handleSort('nome')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Nome <SortIcon columnKey="nome" />
                  </Box>
                </th>

                <th onClick={() => handleSort('livello_permesso_massimo')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Ruolo <SortIcon columnKey="livello_permesso_massimo" />
                  </Box>
                </th>

                <th onClick={() => handleSort('user_type')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Tipo <SortIcon columnKey="user_type" />
                  </Box>
                </th>

                <th onClick={() => handleSort('username')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Username <SortIcon columnKey="username" />
                  </Box>
                </th>

                <th onClick={() => handleSort('codice_fiscale')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    CF <SortIcon columnKey="codice_fiscale" />
                  </Box>
                </th>

                {canEditUtenti() && (
                  <th style={{ textAlign: 'center', width: 160, cursor: 'default' }}>
                    Azioni
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {paginatedUsers.map((user) => {
                const ruolo = ruoloLabels[user.livello_permesso_massimo] || ruoloLabels[0];
                const tipo = userTypeLabels[user.user_type] || userTypeLabels[1];
                const TipoIcon = tipo.icon;

                const current = user.user_status ?? user.status ?? 0;
                const statusDot = current === 1 ? 'success.solidBg' : 'danger.solidBg';

                return (
                  <tr key={user.id}>
                    <td>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: statusDot,
                          }}
                        />
                        <Typography level="body-md" fontWeight="lg">
                          {user.nome}
                        </Typography>
                      </Box>
                    </td>

                    <td>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.4,
                          borderRadius: 'xl',
                          fontSize: 'sm',
                          fontWeight: 'md',
                          bgcolor: `${ruolo.color}.solidBg`,
                          color: '#fff',
                        }}
                      >
                        {ruolo.label}
                      </Box>
                    </td>

                    <td>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TipoIcon size={16} />
                        <Typography level="body-sm">{tipo.label}</Typography>
                      </Box>
                    </td>

                    <td>{user.username}</td>
                    <td>{user.codice_fiscale || '—'}</td>

                    {canEditUtenti() && (
                      <td style={{ textAlign: 'center' }}>
                        <Box display="flex" justifyContent="center" gap={1}>
                          <IconButton
                            size="sm"
                            variant="soft"
                            color="primary"
                            onClick={() => navigate(`/utenti/${user.id}`)}
                            title="Modifica utente"
                          >
                            <Pencil size={16} />
                          </IconButton>

                          <IconButton
                            size="sm"
                            variant="soft"
                            color={current === 1 ? 'danger' : 'success'}
                            onClick={() => handleToggleStatus(user)}
                            title={current === 1 ? 'Disattiva' : 'Attiva'}
                          >
                            <Ban size={16} />
                          </IconButton>

                          <IconButton
                            size="sm"
                            variant="soft"
                            color="danger"
                            onClick={() => {
                              setUserToDelete(user);
                              setConfirmDeleteOpen(true);
                            }}
                            title="Rimuovi dall'organizzazione"
                          >
                            <Trash size={16} />
                          </IconButton>
                        </Box>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Sheet>

      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Button
            size="sm"
            variant="outlined"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            startDecorator={<ChevronLeft size={14} />}
          >
            Precedente
          </Button>

          <Typography level="body-sm">
            Pagina {page} di {totalPages}
          </Typography>

          <Button
            size="sm"
            variant="outlined"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            endDecorator={<ChevronRight size={14} />}
          >
            Successiva
          </Button>
        </Box>
      )}

      <ModaleUtente
        open={showModale}
        onClose={() => setShowModale(false)}
        onSuccess={() => {
          fetchUsers();
          setShowModale(false);
          toast.success('Utente aggiunto/modificato con successo', {
            position: 'top-right',
            autoClose: 2500,
          });
        }}
      />

      <ModaleConferma
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Conferma eliminazione"
        message={
          <>
            Sei sicuro di voler rimuovere <b>{userToDelete?.nome || 'questo utente'}</b>?
          </>
        }
      />
    </Box>
  );
}